import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { ComponentItem } from "designSystem/types/component-item-types";
import { rightPaneStore, sessionStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { action, computed, makeObservable, observable } from "mobx";
import { ChangeEvent } from "react";
import { Channel, Team, User, UserProvider } from "../api";
import { ReactionStore } from "csi/utility/mobxUtility";

export enum UpdateChannelOnNodeOption {
	ADD,
	REMOVE,
}

/** update channel attribute on the node */
export async function updateChannelOnNode(nodeUrl: string, channelId: string, action: UpdateChannelOnNodeOption, chatAttrId: string): Promise<void> {
	const add = action == UpdateChannelOnNodeOption.ADD;
	const remove = action == UpdateChannelOnNodeOption.REMOVE;
	const w = new Writer();
	const cn = w.changeNode(nodeUrl);
	cn.changeAttribute(chatAttrId, xml.ATTR_STRINGSET, [channelId], add, remove);
	try {
		await sessionStore.submitWriter(w);
	} catch (err) {
		console.warn("Failed to save channel to the C8 Node.", err);
	}
}

export class ChatStore<T extends Team, U extends User, C extends Channel, E extends Error, P extends UserProvider<U>, Panel> extends ReactionStore {
	@observable.ref _channels: C[];
	@observable _currentPanel: Panel;
	@observable _channelIsPrivate: boolean = false;
	@observable _channelNameErrorResource: string;
	@observable _channelDescriptionErrorResource: string;
	@observable _newChannelDisplayName: string = "";
	@observable _newChannelDescription: string = "";
	@observable _newTeamId: string;
	@observable.ref _panelHistory: Panel[] = [];
	@observable.ref _selectedUsers: ComponentItem[] = null;
	@observable _teamIdErrorResource: string;
	@observable.ref _teams: T[];
	@observable.ref _users: U[] = null;

	/** the channel list */
	get channels(): C[] {
		return this._channels;
	}

	get channelIsPrivate(): boolean {
		return this._channelIsPrivate;
	}

	/** the resource of the error due to channel name */
	get channelNameErrorResource(): string {
		return this._channelNameErrorResource;
	}

	/** the resource of the error due to channel description */
	get channelDescriptionErrorResource(): string {
		return this._channelDescriptionErrorResource;
	}

	/** the current active page in the panel */
	@computed get currentPanel(): Panel {
		return null;
	}

	/** get the current user detail information */
	@computed get currentUserDetail(): C8Node {
		return sessionStore.userDetails;
	}

	/** indicate whether has any error(validation error, api error...) */
	@computed get hasError(): boolean {
		return false;
	}

	/** the resource for the loading placeholder */
	@computed get loadingSubtitleRes(): string {
		return "";
	}

	/** the description of the being created channel */
	get newChannelDescription(): string {
		return this._newChannelDescription;
	}

	/** the name of the being created channel */
	get newChannelDisplayName(): string {
		return this._newChannelDisplayName;
	}

	/** the id of the team which the channel will be created for */
	get newTeamId(): string {
		return this._newTeamId;
	}

	/** if true, the add people field would only show when channel is private */
	@computed get onlyShowAddPeopleWhenPrivate(): boolean {
		return false;
	}

	/** panel history */
	get panelHistory(): Panel[] {
		return this._panelHistory;
	}

	/** ready to create channel */
	@computed get readyToCreateChannel(): boolean {
		return false;
	}

	/** ready to read channel history */
	@computed get readyToReadChannelHistory(): boolean {
		return false;
	}

	/** the picked users for the channel being get created */
	get selectedUsers(): ComponentItem[] {
		return this._selectedUsers;
	}

	/** indicate if disable the AddPeople dropdown */
	get shouldDisableAddPeople(): boolean {
		const { newTeamId, userDictionary } = this;
		return !newTeamId && !userDictionary;
	}

	/** if true, need to show add people field */
	get showAddPeopleConditionally(): boolean {
		const { onlyShowAddPeopleWhenPrivate, channelIsPrivate } = this;
		return !onlyShowAddPeopleWhenPrivate || (onlyShowAddPeopleWhenPrivate && channelIsPrivate);
	}

	/** the localization resource of team field */
	get teamFieldLabelRes(): string {
		return "Team";
	}

	/** team id error resource */
	get teamIdErrorResource(): string {
		return this._teamIdErrorResource;
	}

	/** the teams which this current user is part of  */
	@computed get teams(): ComponentItem[] | undefined {
		return null;
	}

	/** return chat users provider */
	@computed get userProvider(): P {
		return null;
	}

	/** original users */
	get users(): U[] {
		return this._users;
	}

	/** users dictionary to serve component for the new being create channel */
	@computed get userDictionary(): ComponentItem[] {
		const { users } = this;
		return users.map(({ id, name }) => ({ id, name }));
	}

	@action async _closeRightPane(): Promise<void> {
		rightPaneStore.closeSelected();
	}

	/** close the right pane */
	closeRightPane = (): void => {
		this._closeRightPane();
	};

	@action async _handleOnSaveNewChannel(): Promise<boolean> {
		return true;
	}

	/** handler for save channel */
	handleOnSaveNewChannel = (): Promise<boolean> => {
		return this._handleOnSaveNewChannel();
	};

	/** initialization */
	@action initialize(): void {

	}

	/** Get a new panel from history. If the history is empty, close the right pane. */
	handleOnBack = (): void => {
		const { panelHistory, navigateToPanel, closeRightPane } = this;
		const [newPanel, ...newPanelHistory] = panelHistory;

		if (newPanel != null) {
			navigateToPanel(newPanel, newPanelHistory);
		} else {
			closeRightPane();
		}
	};

	/** Push the current panel into a history and switch to a new panel */
	@action navigateToPanel = (newWidgetPanel: Panel, newPanelHistory?: Panel[]): void => {
		const { setCurrentPanel, setPanelHistory, currentPanel, panelHistory } = this;

		// push the current panel as a first element if the new history is not provided
		setPanelHistory(newPanelHistory ?? [currentPanel, ...panelHistory]);
		setCurrentPanel(newWidgetPanel);
	};

	@action _setCurrentPanel(currentPanel: Panel): void {
		// can be different per store, implement in derived store
		this._currentPanel = currentPanel;
	}

	/** set a specific panel */
	setCurrentPanel = (currentPanel: Panel): void => {
		this._setCurrentPanel(currentPanel);
	};

	@action _setPanelHistory(history: Panel[]): void {
		// can be different per store, implement in derived store
		this._panelHistory = history;
	}

	/** set the panel history */
	setPanelHistory = (history: Panel[]): void => {
		this._setPanelHistory(history);
	};

	@action _setSelectedUsers(event: React.ChangeEvent<{}>, selectedUsers: ComponentItem[], reason: AutocompleteChangeReason): void {
		// can be different per store, implement in derived store
		this._selectedUsers = selectedUsers;
	}

	/** set selected Users */
	setSelectedUsers = (event: React.ChangeEvent<{}>, selectedUsers: ComponentItem[], reason: AutocompleteChangeReason): void => {
		this._setSelectedUsers(event, selectedUsers, reason);
	};

	@action _setTeam(event: React.ChangeEvent<{}>, teamItem: ComponentItem, reason: AutocompleteChangeReason): void {
		// can be different per store, implement in derived store
		this._newTeamId = teamItem.id;
	}

	/** set the team id being created */
	setTeam = (event: React.ChangeEvent<{}>, teamItem: ComponentItem, reason: AutocompleteChangeReason): void => {
		this._setTeam(event, teamItem, reason);
	};

	@action _validateChannelDescription(): boolean {
		// can be different per store, implement in derived store
		return false;
	}

	/** validate the channel description */
	validateChannelDescription = (): boolean => {
		return this._validateChannelDescription();
	};

	@action _validateChannelName(): boolean {
		// can be different per store, implement in derived store
		return false;
	}

	/** validate channel name */
	validateChannelName = (): boolean => {
		return this._validateChannelName();
	};

	@action async _fetchChannels(): Promise<void> {
		// can be different per store, implement in derived store
	}

	/** fetch the channels */
	fetchChannels = async (): Promise<void> => {
		this._fetchChannels();
	};

	@action async _fetchTeams(): Promise<void> {
		// can be different per store, implement in derived store
	}

	/** fetch the teams */
	fetchTeams = async (): Promise<void> => {
		await this._fetchTeams();
	};


	@action async _postMessage(content: string, urlToUnfurl?: string): Promise<boolean> {
		return true;
	}

	/** post message */
	postMessage = async (content: string, urlToUnfurl?: string): Promise<boolean> => {
		// can be different per store, implement in derived store
		return this._postMessage(content, urlToUnfurl);
	};

	@action _setChannelIsPrivate(event: ChangeEvent<HTMLInputElement>, isPrivate: boolean): void {
		// can be different per store, implement in derived store
		this._channelIsPrivate = isPrivate;
	}

	/** set whether or not channel is private */
	setChannelIsPrivate = (event: ChangeEvent<HTMLInputElement>, isPrivate: boolean): void => {
		this._setChannelIsPrivate(event, isPrivate);
	};

	@action async _fetchUsers(): Promise<void> {
		// can be different per store, implement in derived store
	}

	/** fetch the users */
	fetchUsers = async (): Promise<void> => {
		this._fetchUsers();
	};

	@action _setNewChannelName(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		// can be different per store, implement in derived store
		const newName = event.target.value;
		this._newChannelDisplayName = newName;
		this.validateChannelName();
	}

	/** set the name of channel */
	setNewChannelName = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this._setNewChannelName(event);
	};

	@action _setNewChannelDescription(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		// can be different per store, implement in derived store
		const newDescription = event.target.value;
		this._newChannelDescription = newDescription;
		this.validateChannelDescription();
	}

	/** set the description of channel */
	setNewChannelDescription = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		this._setNewChannelDescription(event);
	};

	constructor() {
		super();
		makeObservable(this);
		this.initialize();
	}
}